/*
 * Copyright(c) 1995 - 2020 T-Systems Multimedia Solutions GmbH
 * Riesaer Str. 5, 01129 Dresden
 * All rights reserved.
 */

import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, Output } from '@angular/core';
import { Subject, Subscription, of } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { LanguageService } from '../i18n/language.service';
import { Captcha } from './captcha';
import { I18N } from './captcha.i18n';
import { CaptchaService } from './captcha.service';

/**
 * This component renders a captcha picture from the CaptchaService and a button for its reload.
 * The picture will be rendered on start and on reload button.
 * HTTP Errors from CaptchaService will be suppressed.
 *
 * !Note! In order to use the component CAPTCHA_URI should be defined. See {@link CaptchaService}
 *
 * Restart the captcha outside component:
 * <pre>
 * <common-captcha (captcha)="captcha = $event" #commonCaptcha="commonCaptcha"></common-captcha>
 * <button (click)="commonCaptcha.reload()">Reload outside component</button>
 * </pre>
 */
@Component({
    selector: 'common-captcha',
    exportAs: 'commonCaptcha',
    template: `
        <div class="captchaContainer">
            <img *ngIf="image$ | async as image"
                 class="captchaImg" id="captcha" [src]="image" alt="Captcha"/>
            <button (click)="onReloadClick()" title="{{i18n.captureReload.hoverTxt}}" type="button" ibtn="reload"
                    ibtnTooltip></button>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CaptchaComponent implements OnDestroy, OnInit {
    @Output() captcha = new Subject<Captcha>();

    image$ = this.captcha.pipe(
        map(c => c.image),
        map(image => `data:image/png;base64,${image}`)
    );

    i18n: {
        captureReload: {
            hoverTxt: string
        }
    };

    private onReload$ = new Subject<void>();

    private readonly languageSubscription: Subscription;

    constructor(
        private captchaService: CaptchaService,
        private languageService: LanguageService
    ) {
        this.languageSubscription = languageService.registerForLanguageChanged(this, 'i18n', I18N);
    }

    ngOnInit(): void {
        this.onReload$.pipe(
            startWith(1), // show captcha on start
            switchMap(_ => this.captchaService.getCaptcha().pipe(
                catchError(_ => of(null))
            ))
        )
            .subscribe(this.captcha)
    }

    ngOnDestroy(): void {
        this.onReload$.complete();
        if (this.languageSubscription) this.languageSubscription.unsubscribe();
    }

    onReloadClick(): void {
        this.onReload$.next();
    }

    reload(): void {
        this.onReload$.next();
    }
}
